import axiosNest from '../api/axiosNest';
import { toErrorMessage } from '../admin/userOffers/orderActionsApi';

/**
 * Szczegóły wymagane przez importer VIA (W10) — odczyt i zapis.
 *
 * Odpowiada `apps/api/src/via/zapis-via.controller.ts`. Nagłówek zlecenia
 * pokrywa 6 z około 25 pól importera; resztę uzupełnia tu operator, bo
 * ekstrakcja z maila jeszcze nie działa.
 */

export interface WriteStation {
  id?: string | null;
  companyName?: string | null;
  address?: string | null;
  zip?: string | null;
  city?: string | null;
  countryId?: string | null;
  fromDate?: string | null;
  fromTime?: string | null;
  untilDate?: string | null;
  untilTime?: string | null;
  transportUnitName?: string | null;
  transportUnitCount?: number | null;
}

export interface OrderingParty {
  idZewnetrzny?: string | null;
  nazwa?: string | null;
  adres?: string | null;
  kod?: string | null;
  miasto?: string | null;
  kraj?: string | null;
}

/**
 * Czynność wózkowa — drugi plik dla VIA (16.10.2026, odpowiedzi
 * Marathonu/Euvic, pyt. 39e). Cenę i liczbę czynności wpisuje operator.
 */
export interface ForkliftDetails {
  price?: number | null;
  count?: number | null;
}

export interface WriteDetails {
  zleceniodawca?: OrderingParty | null;
  objetosc?: number | null;
  stacje?: WriteStation[] | null;
  forklift?: ForkliftDetails | null;
}

/**
 * Pola zlecenia zmieniane w tym samym formularzu (16.10.2026): numer
 * zamówienia klienta (pyt. 27 — bez niego nie ma pliku) i wymagany typ
 * pojazdu (pyt. 39e — „+FORKLIFT” to drugi plik).
 */
export interface WriteHeader {
  customerOrderNumber: string | null;
  requiredVehicleType: string | null;
}

export interface WriteDetailsRead {
  orderId: number;
  state: string;
  details: WriteDetails;
  /** Brak u starego API — formularz pokazuje wtedy puste pola. */
  header?: WriteHeader;
  /** Czy zapis wytworzy drugi plik — czynność wózkową. */
  forkliftRequired?: boolean;
  canEdit: boolean;
  editBlockReason: string | null;
}

export interface WriteDetailsProposal {
  orderId: number;
  details: WriteDetails;
  sources: string[];
}

export interface WriteDetailsSaveResult {
  orderId: number;
  state: string;
  details: WriteDetails;
  header?: WriteHeader;
  /** Zmiana danych po zatwierdzeniu unieważnia akceptację. */
  approvalRevoked: boolean;
  /** Plik z poprzednią treścią już leży w skrzynce — trzeba go wytworzyć ponownie. */
  fileOutdated?: boolean;
}

const path = (id: number | string) => `/via-write/${id}/details`;

export async function fetchWriteDetails(
  id: number | string,
): Promise<WriteDetailsRead> {
  const { data } = await axiosNest.get<WriteDetailsRead>(path(id));
  return data;
}

/** Propozycja z danych, które już mamy. Niczego nie zapisuje. */
export async function fetchWriteDetailsProposal(
  id: number | string,
): Promise<WriteDetailsProposal> {
  const { data } = await axiosNest.get<WriteDetailsProposal>(
    `${path(id)}/suggestion`,
  );
  return data;
}

/** Szczegóły i nagłówek idą jednym zapisem — API czyta klucze nagłówka obok `structure`. */
export async function saveWriteDetails(
  id: number | string,
  details: WriteDetails,
  header?: WriteHeader,
): Promise<WriteDetailsSaveResult> {
  const { data } = await axiosNest.put<WriteDetailsSaveResult>(path(id), {
    ...details,
    ...(header ?? {}),
  });
  return data;
}

/** Czy typ ma składnik FORKLIFT (`JUMBO TRUCK+FORKLIFT`) — lustro `requiresForklift` z API. */
export function typeNeedsForklift(type: string | null | undefined): boolean {
  if (typeof type !== 'string') return false;
  return type
    .toUpperCase()
    .split('+')
    .map((s) => s.trim())
    .includes('FORKLIFT');
}

export { toErrorMessage };
